import { hooks, variables, Flex, FlexProps } from '@bitrise/bitkit';
import {
  SeriesPointXY,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGAxisX,
  VizSVGAxisY,
  VizSVGGridLinesAxisX,
} from '@bitrise/bitviz';
import { TypeAttribute, TypeMetricKey } from '@trace/types';
import React, {
  memo,
  MouseEvent,
  PointerEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { METRIC_UNIT_BY_KEY } from '../../../../constants/Metrics';
import { TypeFilterLabel } from '../../../Filters/useFilters';
import { HookResult } from '../../../Performance/usePercentileMetric';
import getFormattedMetricValue from '../../../../utils/metric/getFormattedValue';
import { AttributesContext } from '../../../Attributes/Attributes';
import { colorScale1, colorScale3, scaleZero } from '../../colorScales';
import MessageFailing from '../../MessageFailing';
import MessageFetching from '../../MessageFetching';
import transition from '../../transition';
import MinMaxSeries, { SERIES_HEIGHT } from './MinMaxSeries';
import { useMinMaxSync } from './MinMaxSync';
import MinMaxTooltip from './MinMaxTooltip';
import getMinMaxSeries, { MinMaxPoint, MinMaxDomain } from './getMinMaxSeries';

const { useResizeObserver } = hooks;

const ARTBOARD_PADDING_HORZ = 12;
const ARTBOARD_PADDING_HORZ_FILTERS = ARTBOARD_PADDING_HORZ + 32;
const ARTBOARD_PADDING_VERT = 24;
const DOMAIN_PADDING = 0;

export interface MinMaxChartProps
  extends FlexProps,
    Pick<HookResult, 'metrics' | 'isError' | 'isFetching'> {
  colorScaleBackground?: (v: number) => string;
  colorScaleDiamond?: (v: number) => string;
  filters?: TypeFilterLabel[];
  groupBy?: TypeAttribute;
  isFetched: boolean;
  metric: TypeMetricKey;
  onSeriesClick?: (event: MouseEvent, name: string) => void;
}

function MinMaxChart(props: MinMaxChartProps) {
  const {
    colorScaleBackground = colorScale1,
    colorScaleDiamond = colorScale3,
    isError,
    isFetched,
    isFetching,
    filters,
    groupBy,
    metric,
    metrics,
    refetch,
    onSeriesClick,
    ...rest
  } = props;

  const [[series, seriesXDomain], setSeries] = useState<[MinMaxPoint[], MinMaxDomain]>([
    [],
    [0, 1],
  ]);
  const [focusPoint, setFocusPoint] = useState<SeriesPointXY | null>(null);
  const [axisSize, axisRef] = useResizeObserver();
  const { getAttributeValuesForMetric } = useContext(AttributesContext);
  const attributeValues = getAttributeValuesForMetric(metric, groupBy);
  const { xDomain, yDomain } = useMinMaxSync(series.length, seriesXDomain);
  const [xLower, xUpper] = xDomain;
  const xMiddle = xLower + (xUpper - xLower) / 2;
  const height = Math.max(0, yDomain.length * SERIES_HEIGHT * (1 + DOMAIN_PADDING * 2));

  const seriesFocusPoint =
    focusPoint && focusPoint.y !== null ? series[focusPoint.y] : { avg: 0, min: 0, max: 0 };

  const handleOnPointerMove = useCallback(
    (_: PointerEvent, point: SeriesPointXY) => {
      if (point.y !== null && series[point.y]) {
        setFocusPoint({
          x: point.y !== null ? series[point.y].avg : xMiddle,
          y: point.y,
        });
      }
    },
    [series, xMiddle],
  );

  useEffect(() => {
    setSeries(getMinMaxSeries(attributeValues, metrics));
  }, [attributeValues, metrics]);

  useEffect(() => {
    if ((isError || isFetching) && isFetched) {
      setSeries(([currentSeries, currentXDomain]) => [
        currentSeries.map(({ title }) => ({
          avg: xMiddle,
          max: xMiddle,
          min: xMiddle,
          title,
        })),
        currentXDomain,
      ]);
    }
  }, [isError, isFetching, isFetched, xDomain, xMiddle]);

  return (
    <Flex {...rest} container margin="x2">
      <Viz
        height={ARTBOARD_PADDING_VERT * 2 + height}
        onPointerLeave={() => setFocusPoint(null)}
        onPointerMove={handleOnPointerMove}
        paddingLeft={axisSize.width + ARTBOARD_PADDING_HORZ}
        paddingRight={filters ? ARTBOARD_PADDING_HORZ_FILTERS : ARTBOARD_PADDING_HORZ}
        paddingTop={ARTBOARD_PADDING_VERT}
        transition={transition}
        xDomain={xDomain}
        yDomain={yDomain}
        yDomainPaddingInner={DOMAIN_PADDING}
        yDomainPaddingOuter={DOMAIN_PADDING}
        yDomainScale="band"
      >
        <VizSVG>
          {series.map((point, index) => (
            <MinMaxSeries
              {...point}
              fillBackground={colorScaleBackground(scaleZero(index, series.length))}
              fillDiamond={colorScaleDiamond(scaleZero(index, series.length))}
              filtered={filters && !!filters.find((filter) => filter.value === point.title)}
              key={point.title}
              onClick={
                onSeriesClick && point.title
                  ? (event) => onSeriesClick(event, point.title)
                  : undefined
              }
              opacity={isFetching || isError ? 0 : 1}
              y={index}
            />
          ))}

          <VizSVGGridLinesAxisX
            animate
            stroke={variables.colorGray5}
            strokeDasharray="4 8"
            style={{ pointerEvents: 'none' }}
            tickValues={isFetching || isError ? xDomain : [xLower, xMiddle, xUpper]}
          />

          <VizSVGAxisX
            align="end"
            animate
            fill={variables.colorGray6}
            flip
            format={(x) => getFormattedMetricValue(METRIC_UNIT_BY_KEY[metric], x)}
            stroke={variables.colorGray5}
            style={{ pointerEvents: 'none' }}
            tickLabelOffset={8}
            tickLineLength={1}
            tickValues={isFetching || isError ? xDomain : [xLower, xMiddle, xUpper]}
            ticksOnly
          />

          <VizSVGAxisY
            animate
            fill={variables.colorGrape5}
            flip
            fontWeight={filters ? 'bold' : undefined}
            format={(y) => series[y] && series[y].title}
            ref={axisRef}
            stroke={variables.colorGray5}
            style={{ pointerEvents: 'none' }}
            tickLabelOffset={8}
            tickLineLength={1}
            ticksOnly
          />
        </VizSVG>

        <VizHTML style={{ pointerEvents: 'none' }}>
          <VizHTMLArtboard>
            <MessageFetching fetching={isFetching} />
            <MessageFailing failing={isError} onFetch={refetch} />
          </VizHTMLArtboard>
        </VizHTML>

        <MinMaxTooltip
          {...seriesFocusPoint}
          color={
            focusPoint && focusPoint.y !== null
              ? colorScaleDiamond(scaleZero(focusPoint.y, series.length))
              : variables.colorGray6
          }
          point={focusPoint}
          unit={METRIC_UNIT_BY_KEY[metric]}
          visible={!!focusPoint && !isError && !isFetching}
        />
      </Viz>
    </Flex>
  );
}

export default memo(MinMaxChart);
